import { ref, inject, type ComputedRef, computed } from 'vue'
import { requestStream } from '@lib/util/network'
import useCanvas from '@canvas-builder/composables/useCanvas'
import type { ComponentNode } from '@canvas-builder/types/canvas'
import { cloneDeep } from 'lodash-es'

interface ApiResponse {
  content?: {
    data: ComponentNode[]
  }
  message?: string
  error?: string
}

export default function useAiPrompt() {
  const isProcessing = ref(false)
  const lastPrompt = ref('')
  const apiAbortController = ref<AbortController | null>(null)
  const { canvasContent, activeCanvas, save } = useCanvas()

  const apiUrl = 'https://canvas-pia.pitcher.com/process'

  // Get the token from auth service or use a temporary token for development
  const token = inject<ComputedRef<string>>(
    'authToken',
    computed(() => ''),
  )

  async function processAiPrompt(prompt: string): Promise<void> {
    if (!prompt) return

    // Cancel any ongoing request
    if (apiAbortController.value) {
      apiAbortController.value.abort()
    }

    // Create a new abort controller for this request
    apiAbortController.value = new AbortController()

    try {
      isProcessing.value = true
      lastPrompt.value = prompt

      const requestBody = {
        question: prompt,
        existingContent: {
          name: activeCanvas.value?.name || 'Canvas',
          content: {
            data: canvasContent.value,
          },
        },
      }

      const responseText = await requestStream(
        apiUrl,
        requestBody,
        token.value,
        (chunk) => {
          // Process streaming chunks if needed
          console.log('Processing chunk:', chunk.length)
        },
        apiAbortController.value.signal,
      )

      // Parse the response
      const response: ApiResponse = JSON.parse(responseText)

      if (response.error) {
        throw new Error(response.error)
      }

      // Update canvas content with the AI-generated content
      if (response.content?.data && activeCanvas.value) {
        // Make a deep clone to ensure reactivity
        const newContent = cloneDeep(response.content.data)

        // Update the canvas content in the UI
        canvasContent.value = newContent

        // Direct save to backend to ensure changes are persisted
        if (activeCanvas.value?.id) {
          try {
            await save(
              activeCanvas.value.id,
              {
                content: {
                  ...(activeCanvas.value?.content ?? {}),
                  data: newContent,
                },
              },
              false, // don't show toast
            )

            console.log('AI changes saved successfully')
          } catch (saveError) {
            console.error('Failed to save AI changes:', saveError)
            throw new Error('Failed to save AI changes')
          }
        } else {
          console.warn('Cannot save AI changes: no active canvas')
        }
      }
    } catch (error) {
      if ((error as DOMException).name === 'AbortError') {
        console.log('AI prompt request was cancelled')
      } else {
        console.error('Error processing AI prompt:', error)
        throw error
      }
    } finally {
      isProcessing.value = false
      apiAbortController.value = null
    }
  }

  // Cancel any ongoing request when component unmounts
  function cancelRequest() {
    if (apiAbortController.value) {
      apiAbortController.value.abort()
      apiAbortController.value = null
    }
  }

  return {
    isProcessing,
    lastPrompt,
    processAiPrompt,
    cancelRequest,
  }
}
